import styled from "styled-components";
import { Star, Quote } from "lucide-react";

const Section = styled.section`
  padding: 96px 16px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"}30;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"}30;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h2};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  max-width: 640px;
  margin: 0 auto;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
`;

const TestimonialCard = styled.div`
  padding: 32px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const QuoteIcon = styled.div`
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light}30;
  margin-bottom: 16px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light}30;
  }
`;

const TestimonialText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
  margin: 0 0 24px 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
`;

const StarIcon = styled(Star)`
  fill: ${({ theme }) => theme.colors.light?.accent || "hsl(48, 96%, 53%)"};
  color: ${({ theme }) => theme.colors.light?.accent || "hsl(48, 96%, 53%)"};
  width: 16px;
  height: 16px;
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
  margin-bottom: 4px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const AuthorRole = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const testimonials = [
  {
    text: "Kliq CRM transformed how our team collaborates. The automation features save us hours every week.",
    author: "Sarah Chen",
    role: "CTO, TechCorp",
    avatar: "SC",
  },
  {
    text: "Best investment we've made. The analytics alone are worth it. Highly recommend!",
    author: "Michael Rodriguez",
    role: "Founder, StartupXYZ",
    avatar: "MR",
  },
  {
    text: "Incredible platform. The integrations are seamless and the support team is top-notch.",
    author: "Emily Johnson",
    role: "Operations Lead, GrowthCo",
    avatar: "EJ",
  },
];

export function TestimonialsSection() {
  return (
    <Section>
      <Container>
        <Header>
          <Title>Loved by teams worldwide</Title>
          <Description>
            Join thousands of companies already using Kliq CRM to streamline their workflows.
          </Description>
        </Header>

        <TestimonialsGrid>
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.author}>
              <QuoteIcon>
                <Quote size={24} />
              </QuoteIcon>
              <Stars>
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} />
                ))}
              </Stars>
              <TestimonialText>"{testimonial.text}"</TestimonialText>
              <Author>
                <Avatar>{testimonial.avatar}</Avatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.author}</AuthorName>
                  <AuthorRole>{testimonial.role}</AuthorRole>
                </AuthorInfo>
              </Author>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
      </Container>
    </Section>
  );
}
